import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { ArrowRight, Truck, Scissors, Upload, Zap } from "lucide-react";

const SIGN_CARDS = [
  { id: "corflute", to: "/corflute", tag: "Outdoor-friendly", title: "5mm Corflute", body: "Lightweight, durable corflute signs for real estate, construction, events, promotions and site signage.", cta: "Order corflute", image: "/products/corflute-sign.webp", tint: "#EAF1FB" },
  { id: "foamboard", to: "/foamboard", tag: "Premium display", title: "5mm Foamboard", body: "Premium foamboard printing for presentations, retail displays, exhibitions and point-of-sale.", cta: "Order foamboard", image: "/products/foamboard-sign.webp", tint: "#FDF1E4" },
  { id: "pvc", to: "/pvc", tag: "Rigid & long-lasting", title: "3mm PVC", body: "Durable white PVC signs for shop fronts, offices, wayfinding and long-term indoor or sheltered outdoor use.", cta: "Order PVC signs", image: "/products/pvc-example.webp", tint: "#EAF6F0" },
  { id: "pullup", to: "/pull-up-banners", tag: "Events & expos", title: "Pull-Up Banners", body: "850 × 2000 mm full-colour banners with retractable stand and carry bag — a flat $150 each.", cta: "Order pull-up banners", image: "/products/pull-up-banner.webp", tint: "#F3EEFB" },
  { id: "coasters", to: "/coasters", tag: "Hospitality & merch", title: "Custom Printed Coasters", body: "Full-colour 95mm round or square coasters on absorbent board — from 500 coasters.", cta: "Order coasters", image: "/products/coasters.webp", tint: "#FBEFEF" },
  { id: "flags", to: "/flags", tag: "Outdoor & events", title: "Feather Flags", body: "Double-sided feather flags in 2.4m, 3m or 4m — complete with pole and base.", cta: "Order feather flags", image: "/products/feather-flags.webp", tint: "#E8F4FA" },
  { id: "aframes", to: "/a-frames", tag: "Footpath signage", title: "Aluminium A-Frames", body: "Double-sided A-Frames with permanent aluminium signs or interchangeable corflute signs.", cta: "Order A-Frames", image: "/products/a-frame.webp", tint: "#FBF5E6" },
  { id: "mediawalls", to: "/media-walls", tag: "Events & launches", title: "Media Walls", body: "Complete media wall kits in six sizes, from 1.5m to 6m wide.", cta: "Order media walls", image: "/products/media-wall.webp", tint: "#EEF0FA" },
];

function SignCard({ id, to, tag, title, body, cta, image, tint }) {
  return (
    <Link to={to} className="group rounded-3xl overflow-hidden border border-[#E6E1DA] flex flex-col transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ background: tint }} data-testid={`signs-choice-${id}`}>
      <div className="aspect-[4/3] overflow-hidden bg-white">
        <img src={image} alt={title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="p-5 flex flex-col gap-2 flex-1">
        <div className="eyebrow !text-[#003DA5]/70">{tag}</div>
        <h2 className="font-display text-xl font-extrabold leading-tight">{title}</h2>
        <p className="text-sm text-slate-600 flex-1">{body}</p>
        <div className="mt-2"><span className="btn-primary !py-2.5 !px-4 text-sm">{cta} <ArrowRight size={16} /></span></div>
      </div>
    </Link>
  );
}

export default function SignsHome() {
  return (
    <div className="App overflow-x-clip">
      <Header />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="hero-backdrop">
          <div className="hero-blob hero-blob--red" />
          <div className="hero-blob hero-blob--blue" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-8 pb-8 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="eyebrow mb-3" data-testid="signs-hero-eyebrow">Kwik Kopy Shellharbour · Professional Signage</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black leading-[1] tracking-tight" data-testid="signs-hero-headline">
              CUSTOM SIGNS. PRINTED LOCAL.{" "}
              <span className="relative inline-block">
                PRICED <span className="text-[#003DA5]">INSTANTLY</span>.
                <span className="absolute left-0 right-0 -bottom-1.5 h-[5px] bg-[#003DA5] rounded-full" aria-hidden="true" />
              </span>
            </h1>
          </div>
          <p className="text-base text-slate-700 max-w-sm lg:text-right">
            Choose your product, size and quantity, upload your artwork and see your price instantly.
          </p>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-6 pb-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 signs-products" data-testid="signs-products">
        {SIGN_CARDS.map((c) => <SignCard key={c.id} {...c} />)}
      </section>

      {/* HOW SIMPLE */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="eyebrow">How it works</div>
        <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-2 mb-10 max-w-xl">Order custom signs in minutes.</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: Upload, title: "Upload artwork", body: "Send us your print-ready design in PDF, PNG, JPG, SVG, AI or EPS." },
            { icon: Zap, title: "Configure size & qty", body: "Pick A4–A0, choose quantity, single or double-sided." },
            { icon: Scissors, title: "Standard or custom cut", body: "Rectangular or cut-to-shape at no additional cost." },
            { icon: Truck, title: "We print & deliver", body: "Produced locally by Kwik Kopy Shellharbour. Delivery or local pickup." },
          ].map((f) => (
            <div key={f.title} className="sf-card p-5">
              <f.icon size={22} className="mb-3" style={{ color: "var(--brand)" }} />
              <div className="font-display font-bold text-lg">{f.title}</div>
              <p className="text-sm text-slate-600 mt-1">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        <div className="sf-card p-8 lg:p-12 grid md:grid-cols-12 gap-6 items-center" data-testid="signs-trust-block">
          <div className="md:col-span-6 lg:col-span-7">
            <div className="eyebrow">Made by Kwik Kopy</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold mt-2 leading-tight">Professional print. Serious production.</h2>
            <p className="mt-4 text-slate-600 max-w-xl">Produced by Kwik Kopy Shellharbour using professional commercial printing equipment and finishing.</p>
          </div>
          <div className="md:col-span-6 lg:col-span-5 grid grid-cols-3 gap-2 lg:gap-3">
            <div className="rounded-2xl bg-[#F7F4F0] px-2 py-4 text-center border border-[#ECE7E0] min-w-0"><div className="font-display font-black text-2xl" style={{ color: "#003DA5" }}>Local</div><div className="text-[11px] lg:text-xs font-semibold text-slate-500 mt-1 leading-tight">Shellharbour</div></div>
            <div className="rounded-2xl bg-[#F7F4F0] px-2 py-4 text-center border border-[#ECE7E0] min-w-0"><div className="font-display font-black text-2xl text-[#111315]">Pro</div><div className="text-[11px] lg:text-xs font-semibold text-slate-500 mt-1 leading-tight">Commercial print</div></div>
            <div className="rounded-2xl bg-[#F7F4F0] px-2 py-4 text-center border border-[#ECE7E0] min-w-0"><div className="font-display font-black text-2xl text-[#003DA5]">Fast</div><div className="text-[11px] lg:text-xs font-semibold text-slate-500 mt-1 leading-tight">Made to order</div></div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
